import { useMemoizedFn, useMount } from 'ahooks';
import { Progress, Tooltip, Typography, Upload } from 'antd';
import { ReloadOutlined, InfoCircleFilled } from '@ant-design/icons';
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from 'antd/lib/upload/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as LogoImg } from 'assets/img/logo.svg';
import axios, { CancelTokenSource } from 'axios';
import {
  AiIcon,
  DefaultFileTypeIcon,
  DocIcon,
  DocxIcon,
  EpsIcon,
  GifIcon,
  IdIcon,
  JpegIcon,
  JpgIcon,
  PdfIcon,
  PngIcon,
  PptIcon,
  PptxIcon,
  PsdIcon,
  RarIcon,
  SvgIcon,
  TiffIcon,
  TifIcon,
  UploadDeleteIcon,
  UploadDownIcon,
  XlsIcon,
  XlsxIcon,
  ZipIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { error, toast } from 'components/RevxMessage';
import {
  CommonUploadFileData,
  FileItem,
  UploadAction,
  UploadFileOptions,
} from 'domains/workspaceDetailDomain';
import { cloneDeep } from 'lodash-es';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from 'hooks';
import { flushHighResAndHandle } from 'utils/canvaHighRes';
import jobService from 'services/jobService';
import workspaceDetailService from 'services/workspaceDetailService';
import {
  JOB_FILE_MAX_NUM,
  PICTURE_REGULAR,
  UPLOAD_ACCEPT,
  UPLOAD_FILE_NAME_MAX_LENGTH,
  UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX,
} from 'utils/constants';
import './index.less';

const CommonUploadFile = ({
  defaultFileList,
  onUploadFileChange,
  showUploadList,
  showUploadButton,
  itemData,
  deleteFileHandler,
  getAllFileHandler,
  saveFileHandler,
  setIsUploading,
  uploadIconBeforeOldFiles = true,
  customerItemRender,
  getUploadFileList,
}: Partial<CommonUploadFileData>) => {
  const [fileList, setFileList] = useState<UploadFile<Partial<FileItem>>[]>(
    defaultFileList || []
  );
  const cancelSources = useRef<{ source: CancelTokenSource; uid: string }[]>(
    []
  );
  const [fileErrorList, setFileErrorList] = useState<string[]>([]);
  const [flushingHighRes, setFlushingHighRes] = useState(false);
  const isUploading = useAppSelector((state) => state.app.isUploading);
  const fileMaxLength = JOB_FILE_MAX_NUM;

  const user = useUser();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const flushHighRes = async () => {
    if (flushingHighRes || !itemData?.id) {
      return;
    }
    const jobId = itemData.id;
    setFlushingHighRes(true);
    try {
      await flushHighResAndHandle(() => jobService.flushHighResByJobId(jobId));
    } finally {
      setFlushingHighRes(false);
    }
  };

  const onCancelUpload = (uid?: string) => {
    cancelSources.current.forEach((item) => {
      if (item.uid === uid) {
        item?.source?.cancel();
      }
    });
  };

  const fileTypeIcon: { [str: string]: ReactElement } = {
    ai: <AiIcon />,
    doc: <DocIcon />,
    docx: <DocxIcon />,
    eps: <EpsIcon />,
    gif: <GifIcon />,
    indd: <IdIcon />,
    jpeg: <JpegIcon />,
    jpg: <JpgIcon />,
    pdf: <PdfIcon />,
    png: <PngIcon />,
    ppt: <PptIcon />,
    pptx: <PptxIcon />,
    psd: <PsdIcon />,
    rar: <RarIcon />,
    svg: <SvgIcon />,
    tif: <TifIcon />,
    tiff: <TiffIcon />,
    xls: <XlsIcon />,
    xlsx: <XlsxIcon />,
    zip: <ZipIcon />,
  };

  useMount(() => {
    return () => {
      dispatch.app.setState({
        isUploading: false,
      });
    };
  });

  useEffect(() => {
    setFileList(defaultFileList || []);
  }, [defaultFileList]);

  const uploadButton = (
    <div className="upload-select">
      <RevxFlexLayout direction="column">
        <span>DRAG & DROP</span>
        <span>OR</span>
        <span>CLICK TO</span>
        <span>UPLOAD</span>
      </RevxFlexLayout>
    </div>
  );

  const handleChange = ({ fileList }: UploadChangeParam<UploadFile<any>>) => {
    if (fileList.length > fileMaxLength) {
      return false;
    }
    if (fileList[0]?.response) {
      fileList[0].thumbUrl = fileList[0].response.thumbUrl;
    }
    if (
      fileList[fileList.length - 1]?.status === 'uploading' &&
      fileList.filter((item) => item.status !== 'uploading').length
    ) {
      if (uploadIconBeforeOldFiles) {
        fileList = [
          ...fileList.filter((item) => item.status === 'uploading').reverse(),
          ...fileList.filter((item) => item.status !== 'uploading'),
        ];
      } else {
        fileList = [
          ...fileList.filter((item) => item.status !== 'uploading'),
          ...fileList.filter((item) => item.status === 'uploading').reverse(),
        ];
      }
    }
    if (typeof setIsUploading === 'function') {
      setIsUploading(
        !!fileList.filter((item) => item.status === 'uploading').length
      );
    }
    setFileList(fileList.filter((item) => item.status !== 'error'));
    getUploadFileList && getUploadFileList(fileList);
  };

  const handleDownload = async (option: any) => {
    await dispatch.app.showLoading();
    const payload = option.response || option;

    const a = document.createElement('a');
    a.setAttribute('download', payload.name);
    a.setAttribute('href', payload.path);
    a.setAttribute('target', '_blank');

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    await dispatch.app.hideLoading();
  };

  const handleRemove = async (option: UploadFile<FileItem>) => {
    const payload = option.response || option;
    if (typeof deleteFileHandler === 'function') {
      const res = await deleteFileHandler(payload);
      if (res === '') {
        return false;
      } else {
        if (onUploadFileChange && typeof onUploadFileChange === 'function') {
          onUploadFileChange(fileList);
        }
        return true;
      }
    }
    return false;
  };

  const handleBeforeUpload = (file: RcFile, choicefileList: RcFile[]) => {
    if (1 + fileList.length > fileMaxLength) {
      error(t('common.fileMaxLengthMessage'));
      return Promise.reject();
    }

    if (file.name.length > UPLOAD_FILE_NAME_MAX_LENGTH) {
      error(
        t('common.fileMaxNameMessage', {
          length: UPLOAD_FILE_NAME_MAX_LENGTH,
        })
      );
      return Promise.reject();
    }

    if (!file.size) {
      error(t('common.fileEmptyMessage'));
      return Promise.reject();
    }

    return true;
  };

  const stepPrint = async (
    path: string,
    maxErrorNum: number,
    resolve: { (value: unknown): void }
  ) => {
    if (maxErrorNum > 20) {
      resolve(true);
    } else {
      try {
        maxErrorNum++;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await axios.get(`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${path}`);
        resolve(true);
      } catch {
        stepPrint(path, maxErrorNum, resolve);
      }
    }
  };

  const handleRequest = async (option: any) => {
    // s3 upload
    dispatch.app.setState({
      isUploading: true,
    });
    const fileUrl = await dispatch.uploadFile.getPresigned([option.file.name]);
    const uploadFileUrl = fileUrl[0].path;

    const source = axios.CancelToken.source();
    cancelSources.current.push({
      source,
      uid: option?.file?.uid,
    });

    const uploadRes = await workspaceDetailService.uploadFileToWorkSpace(
      uploadFileUrl,
      option.file,
      {
        token: source.token,
      },
      (param: ProgressEvent) => {
        option.onProgress({
          percent: Math.ceil((param.loaded / option.file.size) * 99),
        });
      }
    );
    if (!uploadRes || uploadRes.status !== 200) {
      option.onError();
      dispatch.app.setState({
        isUploading: false,
      });
      return false;
    }
    const uploadFileUrlObject = new URL(uploadFileUrl);
    const fileRes = {
      path: uploadFileUrlObject.pathname.substr(1),
      fileName: option.file.name,
      filesLen: fileList.length,
    };

    const fileExtension = option.file.name.split('.').pop() || '';
    if (fileExtension === 'pdf') {
      await new Promise((resolve) =>
        stepPrint(uploadFileUrlObject.pathname.substr(1), 0, resolve)
      );
    }

    // save handle
    if (typeof saveFileHandler === 'function') {
      const fileItem = await saveFileHandler({
        ...fileRes,
        ...itemData,
      });
      option.onSuccess(fileItem);
    }
    const newFileList = cloneDeep(fileList).map((item) => {
      if (item.name === option.file.name) {
        item.url = uploadFileUrlObject.pathname.substr(1);
        item.status = 'done';
        item.response = { jobId: itemData!.id, filesLen: fileList.length };
      }
      return item;
    });
    setFileList(newFileList);
    if (onUploadFileChange && typeof onUploadFileChange === 'function') {
      onUploadFileChange(newFileList);
    }
    dispatch.app.setState({
      isUploading: false,
    });
  };

  const renderFileType = (fileName: string, file: UploadFile<any>) => {
    const fileExtension = fileName.split('.').pop() || '';
    if (fileExtension === 'pdf') {
      if (fileErrorList.includes(fileName)) {
        return (
          <span className="anticon anticon-file">
            {fileTypeIcon[fileExtension]}
          </span>
        );
      } else {
        return (
          <img
            className="ant-upload-list-item-image"
            onError={() => {
              setFileErrorList([...fileErrorList, fileName]);
            }}
            src={`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${
              file.url || file.response?.fileUrl
            }`}
            alt=""
          />
        );
      }
    } else if (Object.keys(fileTypeIcon).includes(fileExtension)) {
      return (
        <span className="anticon anticon-file">
          {fileTypeIcon[fileExtension]}
        </span>
      );
    } else {
      return (
        <span className="anticon anticon-file">
          <DefaultFileTypeIcon />
        </span>
      );
    }
  };

  const errorFileHandler = () => {
    toast(t('common.errorDownFile'));
    if (onUploadFileChange && typeof onUploadFileChange === 'function') {
      onUploadFileChange(fileList);
    }
  };

  const itemRender = (
    originNode: ReactElement,
    file: UploadFileOptions<any>,
    fileList: UploadFile<FileItem>[],
    actions: UploadAction
  ) => {
    // Everyone (Buyer / Supplier / Sourcer) sees the print-ready hint: while a
    // high-resolution file is still being processed (inline warning) vs.
    // already print-ready (hover tooltip). Only the job creator additionally
    // gets the refresh button.
    const hasHighResFlag = file.needHighResolution !== undefined;
    const isHighResProcessing =
      hasHighResFlag && Boolean(file.needHighResolution);
    const isPrintReady = hasHighResFlag && !file.needHighResolution;
    const jobCreatorId =
      itemData && 'customerId' in itemData ? itemData.customerId : undefined;
    const isJobCreator =
      jobCreatorId !== undefined && jobCreatorId === user?.userId;
    const showRefreshHighRes = isHighResProcessing && isJobCreator;
    const doneItem = (
      <div className="ant-upload-list-item ant-upload-list-item-done ant-upload-list-item-list-type-picture-card">
            {showRefreshHighRes && (
              <Tooltip title="Refresh high-resolution PDF">
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    flushHighRes();
                  }}
                  style={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    zIndex: 2,
                    cursor: flushingHighRes ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ReloadOutlined
                    spin={flushingHighRes}
                    style={{ fontSize: 16, color: '#009088' }}
                  />
                </span>
              </Tooltip>
            )}
            <div className="ant-upload-list-item-info">
              <span className="ant-upload-span">
                <div
                  className={`ant-upload-list-item-thumbnail ${
                    !PICTURE_REGULAR.test(file.fileName || file.name || '') ||
                    fileErrorList.includes(file.fileName || file.name)
                      ? 'ant-upload-list-item-file'
                      : ''
                  }`}
                  onClick={() => {
                    axios
                      .get(file.thumbUrl || '')
                      .then((res) => {
                        if (res.status === 200) {
                          window.open(file.thumbUrl);
                        } else {
                          errorFileHandler();
                        }
                      })
                      .catch((error) => {
                        if ((error + '').indexOf('404') >= 0) {
                          errorFileHandler();
                        } else {
                          window.open(file.thumbUrl);
                        }
                      });
                  }}
                >
                  {PICTURE_REGULAR.test(file.fileName || file.name || '') &&
                  !fileErrorList.includes(file.fileName || file.name) ? (
                    <img
                      className="ant-upload-list-item-image"
                      src={file.thumbUrl}
                      alt={file.fileName}
                      onError={() => {
                        setFileErrorList([
                          ...fileErrorList,
                          file.fileName || file.name,
                        ]);
                      }}
                    />
                  ) : (
                    <>
                      {renderFileType(file.fileName || file.name || '', file)}
                    </>
                  )}
                </div>
                <Tooltip title={file.fileName || file.name}>
                  <span className="ant-upload-list-item-name">
                    {file.fileName || file.name}
                  </span>
                </Tooltip>
              </span>
            </div>
            <div className="ant-upload-list-item-actions">
              <button
                type="button"
                className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                onClick={() => {
                  axios
                    .get(file.thumbUrl || '')
                    .then((res) => {
                      if (res.status === 200) {
                        window.open(file.thumbUrl);
                      } else {
                        errorFileHandler();
                      }
                    })
                    .catch((error) => {
                      if ((error + '').indexOf('404') >= 0) {
                        errorFileHandler();
                      } else {
                        window.open(file.thumbUrl);
                      }
                    });
                }}
              >
                <span>
                  <UploadDownIcon />
                </span>
              </button>
              {showUploadButton &&
                file.canDelete !== false &&
                !itemData?.skipArtFile && (
                  <button
                    type="button"
                    className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                    onClick={() => actions.remove(file)}
                    hidden={typeof deleteFileHandler === 'undefined'}
                  >
                    <span>
                      <UploadDeleteIcon />
                    </span>
                  </button>
                )}
            </div>
          </div>
    );

    const doneNode = isHighResProcessing ? (
      <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
        {doneItem}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 4,
            marginTop: 4,
            maxWidth: 104,
          }}
        >
          <InfoCircleFilled
            style={{ color: '#faad14', marginTop: 2, flexShrink: 0 }}
          />
          <Typography.Text
            type="warning"
            style={{ fontSize: 12, lineHeight: 1.3 }}
          >
            Print-ready file processing
          </Typography.Text>
        </div>
      </div>
    ) : isPrintReady ? (
      <Tooltip title="Print-ready">{doneItem}</Tooltip>
    ) : (
      doneItem
    );

    return (
      <>
        {file.status === 'done' && doneNode}
        {file.status === 'uploading' && (
          <div className="ant-upload-list-item ant-upload-list-item-uploading ant-upload-list-item-list-type-picture-card">
            <div className="ant-upload-list-item-info">
              <span className="ant-upload-list-item-thumbnail">
                {/* {t('common.uploading')} */}
                <LogoImg />
              </span>
            </div>
            <div className="ant-upload-list-item-progress">
              <Progress percent={file.percent} size="small" showInfo={false} />
            </div>
            <RevxButton
              className="ant-upload-list-item-cancel"
              brandOrange
              type="primary"
              size="small"
              onClick={() => {
                onCancelUpload(file?.originFileObj?.uid);
              }}
            >
              Cancel
            </RevxButton>
          </div>
        )}
      </>
    );
  };

  const dragOverHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.add('drag-over');
    }
  });
  const dragLeaveHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
  });
  const dropHandler = useMemoizedFn((e) => {
    if (e?.currentTarget?.classList) {
      e.currentTarget.classList.remove('drag-over');
    }
    if (e?.dataTransfer?.files?.length > 1) {
      toast.error(t('common.addOneFile'));
    }
  });

  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const selectDiv = divRef.current?.querySelector(
      '.ant-upload-select'
    ) as HTMLDivElement;

    selectDiv.addEventListener('dragover', dragOverHandler);
    selectDiv.addEventListener('dragleave', dragLeaveHandler);
    selectDiv.addEventListener('drop', dropHandler);

    return () => {
      selectDiv.removeEventListener('dragover', dragOverHandler);
      selectDiv.removeEventListener('dragLeave', dragLeaveHandler);
      selectDiv.removeEventListener('drop', dropHandler);
    };
  });

  return (
    <div className="upload-container" ref={divRef}>
      <Upload
        disabled={isUploading || itemData?.skipArtFile}
        accept={UPLOAD_ACCEPT}
        listType="picture-card"
        fileList={fileList}
        onChange={handleChange}
        customRequest={handleRequest}
        onDownload={handleDownload}
        onRemove={handleRemove}
        beforeUpload={handleBeforeUpload}
        itemRender={customerItemRender || itemRender}
        showUploadList={
          showUploadList || {
            showPreviewIcon: false,
            showDownloadIcon: true,
            showRemoveIcon: !!showUploadButton,
          }
        }
        progress={{
          status: 'active',
        }}
      >
        {showUploadButton &&
          fileList?.length < fileMaxLength &&
          !itemData?.skipArtFile &&
          uploadButton}
      </Upload>
    </div>
  );
};

export default CommonUploadFile;
